import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {colorForTeam} from '../../../../helpers/TeamColorProfile';

const F1List = styled.ul`
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    padding-left: 0;
    font-size: 20px;
    font-weight: 400;
    font-style: italic;
    line-height: 1.2;
    letter-spacing: 0;
    list-style: none;
`;

const RowContentWrapper = styled.li`
    --angle-clip: 10px;
    --clip: polygon(var(--angle-clip) 0, 100% 0, calc(100% - var(--angle-clip)) 100%, 0 100%);

    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;
    margin-bottom: 3px;
    
    > * {
        padding: 3px 15px 0;
        clip-path: var(--clip);
        color: ${props => props.theme.f1.tableTextColor};
        background-color: ${props => props.theme.f1.tableBgColor};
        
        :last-child {
            padding-right: 15px;
        }
    }

    > * + * {
        margin-left: calc(0px - var(--angle-clip));
    }
`;

const Count = styled.span`
    position: relative;
    min-width: ${props => props.minWidth || '56px'};
    display: inline-block;
    ${props => (props.posBadge === 'right') && 'padding-right: 20px;'};
    ${props => (props.hasBadge && props.posBadge !== 'right') && 'padding-left: 20px;'};
    padding-right: ${props => props.innerOffsetRight};
    padding-left: ${props => props.innerOffsetLeft};
    font-weight: 700;
    text-align: ${props => props.textAlign || 'center'};
    color: ${props => props.theme.f1.headerTextColor};
    background-color: transparent;
    box-sizing: border-box;
    ${props => ((props.hasVisuality === undefined) || props.hasVisuality) ? '' : 'opacity: 0;'}

    ::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      display: block;
      z-index: -2;
      clip-path: var(--clip);
      background-color: ${props => props.theme.f1.headerBgColor};
    }

    ::after {
        content: '';
        position: absolute;
        top: 0;
        ${props => (props.posBadge === 'right') ? 'right: 0;' : 'left: 0;'};
        z-index: -1;
        width: 15px;
        height: 100%;
        display: ${props => (props.hasBadge === true) ? 'block' : 'none'};
        background-color: ${props => colorForTeam(props.team, 'color')};
        clip-path: var(--clip);
    }
`;

const CountPrimary = Count.extend`
    font-weight: 400;

    ::before {
      background-color: ${props => props.theme.f1.primaryColor};
    }
`;

const Text = styled.span`
    padding-right: ${props => props.innerOffsetRight};
    padding-left: ${props => props.innerOffsetLeft};
    flex-grow: 1;
    text-align: ${props => props.textAlign || 'left'};
`;

const Diff = styled.span`
    position: relative;
    min-width: ${props => props.minWidth || '56px'};
    display: inline-block;
    font-weight: 700;
    text-align: center;
    box-sizing: border-box;

    ::after {
        content: '';
        width: 0;
        height: 0;
        display: inline-block;
        margin-left: 7px;
        border-style: solid;
    }
    
    ${props => (props.hasDiff === 0) && `
        font-size: 0;
        
        ::after {
            display: none;
        }
    `};

    ${props => (props.hasDiff > 0) ? `
        color: #25c100;

        ::before {
            content: '+';
        }

        ::after {
            border-width: 0 6px 12px 6px;
            border-color: transparent transparent #25c100 transparent;
        }
    ` : `
        color: #dc0000;

        ::after {
            border-width: 12px 6px 0 6px;
            border-color: #dc0000 transparent transparent transparent;
        }
    `}
`;

const IconCarWrapper = styled.div`
    padding-top: 3px;
    padding-right: ${props => props.innerOffsetRight};
    padding-bottom: 3px;
    padding-left: ${props => props.innerOffsetLeft};

    img {
        max-width: 100%;
        height: auto;
    }
`;

const StandingsRowDriver = ({rowData}) => (
    <RowContentWrapper key={rowData.participantId}>
        <Count team={rowData.teamShortName} posBadge='right' hasBadge >{rowData.rank}</Count>
        <Text>{rowData.name}</Text>
        <Text textAlign={'right'} >{rowData.teamName}</Text>
        <Count team={rowData.teamShortName} textAlign='right' minWidth='85px' hasBadge >{rowData.points}</Count>
    </RowContentWrapper>
);

const StandingsRowTeam = ({rowData}) => (
    <RowContentWrapper key={rowData.id}>
        <Count team={rowData.shortName} posBadge='right' hasBadge >{rowData.rank}</Count>
        <Text><strong>{rowData.name}</strong></Text>
        <IconCarWrapper><img src={`http://cdn.tjekscores.dk/f1/cars/${rowData.teamShortName}.png`} alt={rowData.teamShortName} width='84' height='21' /></IconCarWrapper>
        <Count team={rowData.shortName} textAlign='right' minWidth='85px' hasBadge >{rowData.points}</Count>
    </RowContentWrapper>
);

const AfterRaceRowDriver = ({rowData, pos}) => (
    <RowContentWrapper key={rowData.pos}>
        <Count team={rowData.teamShortName} posBadge='right' hasBadge>{pos}</Count>
        <Text>{rowData.driverName}</Text>
        <Text textAlign={'right'}>{rowData.teamName}</Text>
        <CountPrimary team={rowData.teamShortName} minWidth='65px' hasBadge>{(rowData.gpPoints > 0) ? '+' : null}{rowData.gpPoints}</CountPrimary>
    </RowContentWrapper>
);

const AfterRaceRowTeam = ({rowData, pos}) => (
    <RowContentWrapper key={rowData.pos}>
        <Count team={rowData.teamShortName} posBadge='right' hasBadge>{pos}</Count>
        <Text><strong>{rowData.teamName}</strong></Text>
        <IconCarWrapper><img src={`http://cdn.tjekscores.dk/f1/cars/${rowData.teamShortName}.png`} alt={rowData.teamShortName} width='84' height='21' /></IconCarWrapper>
        <CountPrimary team={rowData.teamShortName} minWidth='65px' hasBadge>{(rowData.gpPoints > 0) ? '+' : null}{rowData.gpPoints}</CountPrimary>
    </RowContentWrapper>
);

const StandingsAfterRaceRowDriver = ({rowData}) => (
    <RowContentWrapper key={rowData.pos}>
        <Count team={rowData.teamShortName} posBadge='right' hasBadge>{rowData.pos}</Count>
        <Diff minWidth='75px' hasDiff={rowData.posChange}>{rowData.posChange}</Diff>
        <Text>{rowData.driverName}</Text>
        <Text textAlign={'right'} innerOffsetRight='45px'>{rowData.teamName}</Text>
        <Count team={rowData.teamShortName} minWidth='85px' hasBadge>{rowData.points}</Count>
        <CountPrimary team={rowData.teamShortName} minWidth='64px' hasVisuality={rowData.gpPoints !== 0}>{(rowData.gpPoints > 0) ? '+' : null}{rowData.gpPoints}</CountPrimary>
    </RowContentWrapper>
);

const StandingsAfterRaceRowTeam = ({rowData}) => (
    <RowContentWrapper key={rowData.pos}>
        <Count team={rowData.teamShortName} posBadge='right' hasBadge>{rowData.pos}</Count>
        <Diff minWidth='75px' hasDiff={rowData.posChange}>{rowData.posChange}</Diff>
        <Text><strong>{rowData.teamName}</strong></Text>
        <IconCarWrapper><img src={`http://cdn.tjekscores.dk/f1/cars/${rowData.teamShortName}.png`} alt={rowData.teamShortName} width='84' height='21' /></IconCarWrapper>
        <Count team={rowData.teamShortName} minWidth='85px' hasBadge>{rowData.points}</Count>
        <CountPrimary team={rowData.teamShortName} minWidth='64px' hasVisuality={rowData.gpPoints !== 0}>{(rowData.gpPoints > 0) ? '+' : null}{rowData.gpPoints}</CountPrimary>
    </RowContentWrapper>
);

const renderValRow = (key, value, options = {}, teamShortName = '') => {
    switch (key) {
        case 'pos':
        case 'rank':
        case 'points':
            return (<Count key={(key + Math.random())} team={teamShortName} posBadge={((options.posBadge || (options.posBadge === undefined && (key === 'pos' || key === 'rank'))) ? 'right' : '')} minWidth={(options.minWidth || '56px')} textAlign={(options.textRight === undefined || options.textRight) ? 'right' : ''} innerOffsetRight={(options.innerOffsetRight || '')} innerOffsetLeft={(options.innerOffsetLeft || '')} hasBadge >{options.value || value}</Count>);
        case 'name':
        case 'driverName':
        case 'teamName':
            return (<Text key={(key + Math.random())} textAlign={(options.textRight ? 'right' : '')} innerOffsetRight={(options.innerOffsetRight || '')} innerOffsetLeft={(options.innerOffsetLeft || '')}>{(options.strong ? <strong>{value}</strong> : value)}</Text>);
        case 'shortName':
        case 'teamShortName':
            return (<IconCarWrapper key={(key + Math.random())} innerOffsetRight={(options.innerOffsetRight || '')} innerOffsetLeft={(options.innerOffsetLeft || '')} ><img src={`http://cdn.tjekscores.dk/f1/cars/${value}.png`} alt={value} width='84' height='21' /></IconCarWrapper>);
        case 'gpPoints':
            return (<CountPrimary key={(key + Math.random())} team={teamShortName} minWidth={(options.minWidth || '64px')} hasBadge={options.hasBadge} hasVisuality={options.hasVisuality || value !== 0} textAlign={(options.textRight === undefined || options.textRight) ? 'right' : ''}>{(value > 0) ? '+' : null}{value}</CountPrimary>);
        case 'posChange':
            return (<Diff key={(key + Math.random())} minWidth={(options.minWidth || '75px')} hasDiff={value} >{value}</Diff>);
        default:
            return value;
    }
};

const Row = (props) => {
    const { rowData = {}, spec = [] } = props;

    return (
        <RowContentWrapper>
            {spec.map(key => {
                return (renderValRow(key, rowData[key], props[key], (rowData.teamShortName || rowData.shortName)))
            })}
        </RowContentWrapper>
    );
};

Row.contextTypes = {
    info: PropTypes.object
};

const Container = ({ children }) => (
    <F1List>
        {children}
    </F1List>
);

export default { Container, Row };



/** WEBPACK FOOTER **
 ** ../src/widgets/components/sm/views/f1/F1ListRow.js
 **/